import React, { useEffect, useState } from 'react';
import { STICKY_ROOT } from 'consts/rootNodes';
import ReactDOM from 'react-dom';
import { RootAction, State } from 'store/state';
import { navigateTo } from 'store/state/router/actions';
import { connect } from 'react-redux';
import { NavigateToFn } from 'config/routes';
import { NotificationBannerMessage } from './types';
import { appStateSelector } from 'store/state/app/selectors';
import { Dispatch } from 'redux';
import { setNotificationBannerMessage } from 'store/state/app/actions';
import { useLocale } from 'locale';
import { useSpring, animated } from 'react-spring';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { createSelector } from 'reselect';
import { bannersConfig } from './bannersConfig';
import { useSendEvent } from 'analytics/useSendEvent';

interface NotificationBannerContainerProps {
  navigate: NavigateToFn;
  message: NotificationBannerMessage;
  onClose: () => void;
}

enum BannerState {
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
  HIDING = 'hiding',
}

const DEFAULT_TIMEOUT = 3000;

const NotificationBannerContainer = ({ navigate, message, onClose }: NotificationBannerContainerProps) => {
  const { t } = useLocale();
  const sendEvent = useSendEvent();
  const isMobile = useScreenBreakpoint() < 3;
  const [ bannerState, setBannerState ] = useState(BannerState.HIDDEN);
  const bannerConfig = message ? bannersConfig[message.typeId] : null;

  const animation = useSpring({
    transform: bannerState === BannerState.VISIBLE ? 'translateY(0)' : 'translateY(100%)',
    opacity: bannerState === BannerState.VISIBLE ? 1 : 0,
    onRest: () => {
      if (bannerState === BannerState.HIDING) {
        onClose();
        setBannerState(BannerState.HIDDEN);
      }
    },
  });

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> = null;

    if (bannerConfig) {
      setBannerState(BannerState.VISIBLE);

      timerId = setTimeout(() => {
        setBannerState(BannerState.HIDING);
      }, bannerConfig.timeout || DEFAULT_TIMEOUT);
    }
    return () => clearTimeout(timerId);
  }, [ bannerConfig ]);

  return ReactDOM.createPortal(
    <animated.div
      style={{
        position: 'fixed',
        zIndex: 1000,
        willChange: 'transform, opacity',
        left: 0,
        bottom: 0,
        ...(isMobile ? { right: 0 } : {}),
        ...animation,
      }}
    >
      {bannerConfig ? (
        <bannerConfig.Component
          t={t}
          navigate={navigate}
          sendEvent={sendEvent}
        />
      ) : null}
    </animated.div>,
    document.getElementById(STICKY_ROOT)
  );
};

const messageSelector = createSelector(appStateSelector, ({ notificationBannerMessage }) => notificationBannerMessage);

const mapStateToProps = (state: State) => ({
  message: messageSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  navigate: (...args: Parameters<NavigateToFn>) => {
    dispatch(navigateTo(...args));
    dispatch(setNotificationBannerMessage(null));
  },
  onClose: () => dispatch(setNotificationBannerMessage(null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationBannerContainer);
